import React, { useCallback, useEffect, useRef, useState } from "react";
import { forwardRef } from "react";
import RFB from '@novnc/novnc/lib/rfb.js'
import { cn } from "@/lib/utils";
import { X, AlertCircle, RefreshCw, WifiOff, Check, Lock } from "lucide-react";
// import { toast } from "sonner";

import { refreshScreenCastToken, remoteBrowserEndpoint, remoteBrowserUiEventsEndpoint } from "modules/main/services/endpoints";
import { Button } from "@/components/ui/button";



import {
    Sheet,
    SheetContent,
} from "@/components/ui/sheet"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { isDesktopSize } from "utils/is-desktop";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useSheetBackNavigation } from "hooks/useSheetBackNavigation";
// import { MainLogo } from "modules/main/assets/main_logo";
import { OpenKeyBoardButton } from "./OpenKeyBoardButton";


interface RemoteBrowserProps extends React.HTMLAttributes<HTMLDivElement>{
    store_id:number|null;
    storeName:string|null;
    storeLogo:string|null;
    currentStoreStatus?: "PENDING" | "COMPLETED" | "RESUMING" | "FAILED" | "WAITING" | "STARTED" | "LOGIN_REQUIRED" | "PARTIAL" | "REPLACING_ITEMS" | "VALIDATING_LOGIN" | "ADDING_TO_CART" | "ADDRESS_NOT_SELECTED" | undefined;
    token:string;
    openState:boolean;
    setOpen: (open: boolean) => void;
    onConfirmSessionReady: () => void;
    /** TTL update from a STREAMING_TTL_UPDATE SSE event; used to sync the countdown with the server. */
    streamingTtlUpdate?: { store_id: number; newExpiresAt: string; remainingSeconds: number } | null;
}

type UiEventName =
    | 'TEXT_INPUT_FOCUS'
    | 'TEXT_INPUT_BLUR'
    | 'SHOW_KEYBOARD'
    | 'HIDE_KEYBOARD'
    | 'KEYBOARD_HINT';

interface UiEventMessage {
    v: number;
    type: 'ui_event';
    event: UiEventName;
    session_id?: string;
    ts?: string;
    payload?: {
        in_text_input?: boolean;
        show_keyboard?: boolean;
        [key: string]: unknown;
    };
    in_text_input?: boolean;
    value?: boolean;
    action?: string;
}

interface ReadyMessage {
    v: number;
    type: 'system';
    event: 'READY';
    session_id?: string;
    ts?: string;
    payload: {
        uiToken: string;
        [key: string]: unknown;
    };
}

const parseReadyMessage = (rawData: unknown): ReadyMessage | null => {
    if (typeof rawData !== 'string') return null;

    let parsed: unknown;
    try {
        parsed = JSON.parse(rawData);
    } catch {
        return null;
    }

    if (!parsed || typeof parsed !== 'object') return null;

    const msg = parsed as Record<string, unknown>;
    if (msg.type !== 'system' || msg.event !== 'READY') return null;

    const payload = msg.payload as Record<string, unknown> | undefined;
    if (!payload || typeof payload.uiToken !== 'string') return null;

    return {
        v: typeof msg.v === 'number' ? msg.v : 1,
        type: 'system',
        event: 'READY',
        session_id: typeof msg.session_id === 'string' ? msg.session_id : undefined,
        ts: typeof msg.ts === 'string' ? msg.ts : undefined,
        payload: {
            uiToken: payload.uiToken,
            ...payload,
        },
    };
};

/**
 * Parses a BROWSER_INIT system envelope from the remote browser.
 * @param rawData - Raw WebSocket message data
 * @returns The human-readable status message string, or null if not a BROWSER_INIT envelope
 */
const parseBrowserInitMessage = (rawData: unknown): string | null => {
    if (typeof rawData !== 'string') return null;
    let parsed: unknown;
    try { parsed = JSON.parse(rawData); } catch { return null; }
    if (!parsed || typeof parsed !== 'object') return null;
    const msg = parsed as Record<string, unknown>;
    if (msg.type !== 'system' || msg.event !== 'BROWSER_INIT') return null;
    const payload = msg.payload as Record<string, unknown> | undefined;
    if (!payload || typeof payload.message !== 'string') return null;
    return payload.message;
};

const parseUiEventMessage = (rawData: unknown): UiEventMessage | null => {
    if (typeof rawData !== 'string') return null;

    let parsed: unknown;
    try {
        parsed = JSON.parse(rawData);
    } catch {
        return null;
    }

    if (!parsed || typeof parsed !== 'object') return null;

    const msg = parsed as Record<string, unknown>;
    if (msg.type !== 'ui_event') return null;

    const rawEvent = (typeof msg.event === 'string' ? msg.event : msg.action) as string | undefined;
    if (!rawEvent) return null;

    const allowedEvents: UiEventName[] = [
        'TEXT_INPUT_FOCUS',
        'TEXT_INPUT_BLUR',
        'SHOW_KEYBOARD',
        'HIDE_KEYBOARD',
        'KEYBOARD_HINT',
    ];

    if (!allowedEvents.includes(rawEvent as UiEventName)) return null;

    const payload = (msg.payload && typeof msg.payload === 'object')
        ? (msg.payload as Record<string, unknown>)
        : undefined;

    return {
        v: typeof msg.v === 'number' ? msg.v : 1,
        type: 'ui_event',
        event: rawEvent as UiEventName,
        session_id: typeof msg.session_id === 'string' ? msg.session_id : undefined,
        ts: typeof msg.ts === 'string' ? msg.ts : undefined,
        payload: payload
            ? {
                in_text_input: typeof payload.in_text_input === 'boolean' ? payload.in_text_input : undefined,
                show_keyboard: typeof payload.show_keyboard === 'boolean' ? payload.show_keyboard : undefined,
                ...payload,
            }
            : undefined,
        in_text_input: typeof msg.in_text_input === 'boolean' ? msg.in_text_input : undefined,
        value: typeof msg.value === 'boolean' ? msg.value : undefined,
        action: typeof msg.action === 'string' ? msg.action : undefined,
    };
};

/** Cycling reassurance messages shown between server BROWSER_INIT events */
const REASSURANCE_MESSAGES = [
    'Iniciando navegador \u2013 Ya casi estamos\u2026',
    'Cargando tu sesi\u00f3n, un momento\u2026',
    'Preparando todo para ti\u2026',
    'Conectando con el supermercado\u2026',
] as const;

export const RemoteBrowser = forwardRef<HTMLDivElement, RemoteBrowserProps>(({className,store_id,token,openState,currentStoreStatus,streamingTtlUpdate,onConfirmSessionReady,...props},ref) => {
    // const ws = useRef<WebSocket | null>(null);
    // const viewerRef = useRef<HTMLDivElement | null>(null);
    const isMobile = !useMediaQuery(isDesktopSize);
    const [viewerElement, setViewerElement] = useState<HTMLDivElement | null>(null);
    const rfbRef = useRef<any>(null);
    const [isVncConnected, setIsVncConnected] = useState(false);
    // const [imgSrc, setImgSrc] = React.useState<string>("");
    const [imgNaturalSize, setImgNaturalSize] = useState<{ w: number; h: number } | null>(null);
    const [activeToken, setActiveToken] = useState(token);
    const lastMoveTime = useRef<number>(0);
    // 2. Definimos el límite (50ms = 20 movimientos por segundo)
    const THROTTLE_MS = 50;
    const isPressing = useRef<boolean>(false);
    // Para evitar duplicación de eventos touch/mouse
    const lastTouchTime = useRef<number>(0); 
 
    // const screencastRef = useRef<HTMLImageElement>(null);
    const [keyboardVisible, setKeyboardVisible] = useState(false);
    const hiddenInputRef = useRef<HTMLInputElement>(null);
    const [keyboardFocused, setKeyboardFocused] = useState(false);
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    const [isRemoteTextInputFocused, setIsRemoteTextInputFocused] = useState(false);
    const uiEventsWsRef = useRef<WebSocket | null>(null);
    const [uiToken, setUiToken] = useState<string | null>(null); 
    const [uiChannelReady, setUiChannelReady] = useState(false);
    const uiTokenRef = useRef<string | null>(null);
    // const hasShownVncConnectToastRef = useRef(false);
    const [isConnectionError, setIsConnectionError] = useState(false);
    const [isConnectionInterrupted, setIsConnectionInterrupted] = useState(false);
    const [retryKey, setRetryKey] = useState(0);
    const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    /**
     * Latest store status held in a ref so the RFB WebSocket effect does not
     * re-mount every time the status oscillates between LOGIN_REQUIRED,
     * VALIDATING_LOGIN, ADDRESS_NOT_SELECTED, etc. Tearing the WS down and
     * recreating it on every status change cascades into backend errors
     * (browser destroyed mid-session, token reuse, instance churn).
     */
    const currentStoreStatusRef = useRef(currentStoreStatus);
    useEffect(() => { currentStoreStatusRef.current = currentStoreStatus; }, [currentStoreStatus]);
    /**
     * Latest setupUiEventChannel callback held in a ref so the RFB WebSocket
     * effect does not re-mount each time the callback identity changes.
     */
    const setupUiEventChannelRef = useRef<((tokenToUse: string) => void) | null>(null);
    /** One-time listener that syncs the local CapsLock state to the remote Xvfb on first keystroke. */
    const capsLockSyncListenerRef = useRef<((e: KeyboardEvent) => void) | null>(null);
    /** Dynamic status message shown in the loading overlay during browser init */
    const [initStatusMessage, setInitStatusMessage] = useState<string | null>(null);
    const storeDisplayName = props.storeName?.trim() || "la tienda";
    const shouldShowLoginCompletionBanner = openState && (
        currentStoreStatus === "LOGIN_REQUIRED" || currentStoreStatus === "ADDRESS_NOT_SELECTED"
    ) && isVncConnected;
    /** Timestamp (ms) of the last BROWSER_INIT message received from the server */
    const lastServerMsgTimeRef = useRef<number>(0);
    /** Current index cycling through REASSURANCE_MESSAGES */
    const timerMsgIndexRef = useRef<number>(0);

    /** Timeout in ms before declaring that the VNC connection has failed */
    const CONNECTION_TIMEOUT_MS = 30_000;

    const specialKeysyms: Record<string, number> = {
        CapsLock: 0xffe5,
        Backspace: 0xff08,
        Tab: 0xff09,
        Enter: 0xff0d,
        Escape: 0xff1b,
        Delete: 0xffff,
        ArrowLeft: 0xff51,
        ArrowUp: 0xff52,
        ArrowRight: 0xff53,
        ArrowDown: 0xff54,
        Home: 0xff50,
        End: 0xff57,
        PageUp: 0xff55,
        PageDown: 0xff56,
    };

    const handleClose = useCallback((open: boolean) => {
        props.setOpen(open);
    }, [props.setOpen]);

    useSheetBackNavigation({
        isOpen: openState,
        onRequestClose: () => handleClose(false),
    });

    const sendKeyToRemote = useCallback((key: string) => {
        const rfb = rfbRef.current;
        if (!rfb || typeof rfb.sendKey !== 'function') return;

        const keysym = specialKeysyms[key] ?? (key.length === 1 ? key.codePointAt(0) : undefined);
        if (!keysym) return;

        rfb.sendKey(keysym);
    }, []);

    // Abre el teclado nativo enfocando el input oculto
    const focusHiddenInput = useCallback(() => {
        if (hiddenInputRef.current) {
            hiddenInputRef.current.focus();
            setKeyboardFocused(true);
        }
    }, []);

    // Al recibir HIDE_KEYBOARD, quitamos foco
    const blurHiddenInput = useCallback(() => {
        if (hiddenInputRef.current) {
            hiddenInputRef.current.blur();
            setKeyboardFocused(false);
        }
    }, []);

    const handleViewerTouchEnd = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
        if (!openState || !isTouchDevice) return;

        const target = event.target as HTMLElement | null;
        const isVncViewportTap = !!target?.closest('canvas, .noVNC_canvas, .noVNC_container, .noVNC_screen, .noVNC_wrapper');
        if (!isVncViewportTap) return;

        focusHiddenInput();
    }, [openState, isTouchDevice, focusHiddenInput]);

    useEffect(() => {
        const hasTouch = window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
        setIsTouchDevice(hasTouch);
    }, []);

    // useEffect(() => {
    //     if (!openState) {
    //         hasShownVncConnectToastRef.current = false;
    //     }
    // }, [openState]);
 
    useEffect(() => {
        if (!openState) {
            setKeyboardVisible(false);
            blurHiddenInput();
            return;
        }

        setKeyboardVisible(isTouchDevice);
    }, [openState, isTouchDevice, blurHiddenInput]);

    useEffect(() => {
        if (!isTouchDevice) return;

        if (isRemoteTextInputFocused) {
            setKeyboardVisible(true);
            focusHiddenInput();
            return;
        }

        blurHiddenInput();
    }, [isTouchDevice, isRemoteTextInputFocused, focusHiddenInput, blurHiddenInput]);

    useEffect(() => {
        const element = viewerElement;
        if (!element) return;

        const preventNativeTouch = (e: TouchEvent) => {
            if (e.cancelable) e.preventDefault();
        };
        // Forzamos a que el navegador sepa que NO son pasivos
        element.addEventListener('touchstart', preventNativeTouch, { passive: false });
        element.addEventListener('touchmove', preventNativeTouch, { passive: false });

        return () => {
            element.removeEventListener('touchstart', preventNativeTouch);
            element.removeEventListener('touchmove', preventNativeTouch);
        };
        }, [openState, viewerElement]);
    
        const refreshSessionToken = useCallback(async (store_id:number) => {
            try{
                const newTokenResponse = await fetch(refreshScreenCastToken(store_id), {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include"                            
                });

                if(!newTokenResponse.ok){
                    throw new Error("Failed to refresh token");
                }
                const newTokenData = await newTokenResponse.json();
                const newToken = newTokenData.token;
                setActiveToken(newToken);
            }catch(e){
                console.error("Error requesting new token:", e);
                props.setOpen(false);
            }   
        }, []);

    const setupUiEventChannel = useCallback((tokenToUse: string) => {
        
        if (!openState || !store_id) return;

        // Avoid opening duplicate connections
        const existing = uiEventsWsRef.current;
        if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
            return;
        }

        const ws = new WebSocket(remoteBrowserUiEventsEndpoint(tokenToUse, store_id, isMobile));
        uiEventsWsRef.current = ws;
        uiTokenRef.current = tokenToUse;

        ws.onmessage = (event) => {
            // Handle BROWSER_INIT progress messages (shown in loading overlay)
            const initMsg = parseBrowserInitMessage(event.data);
            if (initMsg) {
                lastServerMsgTimeRef.current = Date.now();
                setInitStatusMessage(initMsg);
                return;
            }

            // Handle UI event messages (keyboard hints, input focus)
            const data = parseUiEventMessage(event.data);
            if (!data) return;

            const payloadTextInput = data.payload?.in_text_input;
            const payloadKeyboardHint = data.payload?.show_keyboard;
            const isTextInputFocused = payloadTextInput ?? data.in_text_input ?? data.value;

            if (data.event === 'TEXT_INPUT_FOCUS' || isTextInputFocused === true) {
                setIsRemoteTextInputFocused(true);
                return;
            }

            if (data.event === 'TEXT_INPUT_BLUR' || isTextInputFocused === false) {
                setIsRemoteTextInputFocused(false);
                return;
            }

            if (data.event === 'SHOW_KEYBOARD' || payloadKeyboardHint === true) {
                setIsRemoteTextInputFocused(true);
                return;
            }

            if (data.event === 'HIDE_KEYBOARD' || payloadKeyboardHint === false) {
                setIsRemoteTextInputFocused(false);
            }
        };

        ws.onerror = (error) => {
            console.error('Remote browser UI events websocket error:', error);
        };

        ws.onclose = () => {
            if (uiEventsWsRef.current === ws) {
                uiEventsWsRef.current = null;
                setUiChannelReady(false);
            }
        };

    }, [openState, store_id, isMobile]);

    // Keep the ref in sync so the RFB WS effect can call the latest version
    // without listing the callback in its dependency array (which would cause
    // a re-mount on every recompute).
    useEffect(() => {
        setupUiEventChannelRef.current = setupUiEventChannel;
    }, [setupUiEventChannel]);

    /**
     * Reassurance timer: emits a cycling message every 3 seconds while the
     * browser is still loading. Skips if a fresh server BROWSER_INIT message
     * arrived in the last 2.5 seconds to avoid overriding it.
     */
    useEffect(() => {
        if (isVncConnected || isConnectionError) return;

        const interval = setInterval(() => {
            const msSinceServer = Date.now() - lastServerMsgTimeRef.current;
            if (msSinceServer < 3500) return;

            const nextIndex = (timerMsgIndexRef.current + 1) % REASSURANCE_MESSAGES.length;
            timerMsgIndexRef.current = nextIndex;
            setInitStatusMessage(REASSURANCE_MESSAGES[nextIndex]);
        }, 4000);

        return () => clearInterval(interval);
    }, [isVncConnected, isConnectionError, retryKey]);

    useEffect(() => {

        if (!viewerElement) {
            return; 
        }

        if (!store_id || !token) {
            return;
        }

        // Reset error state and start the connection timeout on every attempt (including retries)
        setIsConnectionError(false);
        setIsConnectionInterrupted(false);
        if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current);
        }
        connectionTimeoutRef.current = setTimeout(() => {
            setIsConnectionError(true);
        }, CONNECTION_TIMEOUT_MS);

        // Read the resume flag from the ref so this effect does not re-run
        // every time `currentStoreStatus` toggles between LOGIN_REQUIRED,
        // VALIDATING_LOGIN, ADDRESS_NOT_SELECTED, etc. The status snapshot
        // captured at WS-open time is enough.
        const isResumeConnection = currentStoreStatusRef.current === 'ADDRESS_NOT_SELECTED';
        const ws = new WebSocket(remoteBrowserEndpoint(activeToken, store_id, isMobile, isResumeConnection), ['binary']);

        ws.addEventListener('close', (event) => {
            // Token expired — refresh and reconnect; do not show error UI
            if (event.code === 4009 || event.reason === "TOKEN_INVALID") {
                if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
                setIsVncConnected(false); 
                refreshSessionToken(store_id);
                return;
            }

            // Unexpected close — surface error or interruption depending on whether we were connected
            setIsVncConnected((prev) => {
                if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
                if (!prev) {
                    // Never reached VNC — show connection error
                    setIsConnectionError(true);
                } else {
                    // Was connected but server closed the session (e.g. Cloud Run max lifetime)
                    setIsConnectionInterrupted(true);
                }
                return false;
            });
        });        
        
        // Intercept text frames on the rfb WS before noVNC processes them.
        // The READY envelope (which carries the uiToken) is sent as a text
        // frame and must not reach the RFB protocol parser.
        ws.addEventListener('message', (event: MessageEvent) => {
            if (typeof event.data !== 'string') return;
            // Prevent noVNC from attempting to parse this JSON as RFB binary
            event.stopImmediatePropagation();
            const readyMsg = parseReadyMessage(event.data);
            if (readyMsg) {
                const newUiToken = readyMsg.payload.uiToken;
                setUiToken(newUiToken);
                uiTokenRef.current = newUiToken;
                setupUiEventChannelRef.current?.(newUiToken);
            }
        });

        const rfb = new RFB(viewerElement, ws);
        // Display settings
        rfb.scaleViewport = true;
        // Avoid noVNC clipping the viewport on narrow screens
        rfb.clipViewport = false;
        rfb.resizeSession = true;
        rfb.showDotCursor = true;

        // Keep the instance reference for cleanup
        rfbRef.current = rfb;

        const handleConnect = () => {
            // Clear the connection timeout — we are successfully connected
            if (connectionTimeoutRef.current) {
                clearTimeout(connectionTimeoutRef.current);
                connectionTimeoutRef.current = null;
            }
            setIsVncConnected(true);
            // Clear init status and reset timer state — the browser is now fully loaded
            setInitStatusMessage(null);
            lastServerMsgTimeRef.current = 0;
            timerMsgIndexRef.current = 0;

            // Sync CapsLock state with the remote server on the first keystroke.
            // Xvfb always starts with CapsLock OFF. If the local keyboard already has
            // CapsLock ON when the session opens, the states diverge and users would get
            // lowercase when they expect uppercase (or vice-versa).
            // We register a one-time capture listener that checks the local CapsLock state
            // on the very first non-CapsLock keydown and sends a CapsLock toggle to the
            // remote if the states are out of sync.
            const syncCapsLock = (e: KeyboardEvent) => {
                if (e.key === 'CapsLock') return; // skip — toggling itself would double-flip
                document.removeEventListener('keydown', syncCapsLock, true);
                capsLockSyncListenerRef.current = null;
                if (!e.getModifierState('CapsLock')) return; // local CapsLock is OFF — remote matches
                const currentRfb = rfbRef.current;
                if (!currentRfb) return;
                // Local CapsLock is ON but remote Xvfb started OFF — toggle remote to ON.
                currentRfb.sendKey(0xffe5, 'CapsLock', true);
                currentRfb.sendKey(0xffe5, 'CapsLock', false);
            };
            document.addEventListener('keydown', syncCapsLock, true);
            capsLockSyncListenerRef.current = syncCapsLock;

            // if (!hasShownVncConnectToastRef.current) {
            //     toast.custom(() => (
            //         <div className="w-full text-xs rounded-lg border bg-popover px-4 py-3 text-popover-foreground shadow-lg">
            //             <div className="flex flex-col gap-2">
            //                 <div className="flex flex-row items-end gap-2">
            //                     <MainLogo classname="h-5 w-5" />
            //                     <span className="font-extrabold">¡Casi listos!</span>
            //                 </div>
            //                 <p className="text-xs text-muted-foreground">
            //                     Inicia sesión y asegúrate de elegir tu ubicación de entrega. Cuando termines, simplemente cierra esta ventana y comenzaremos a crear tu carrito.
            //                 </p>
            //             </div>
            //         </div>
            //     ),{closeButton: true, duration: 10000});
            //     hasShownVncConnectToastRef.current = true;
            // }
        };

        const handleDisconnect = (e: unknown) => {
            // Remove any pending CapsLock sync listener — session is gone.
            if (capsLockSyncListenerRef.current) {
                document.removeEventListener('keydown', capsLockSyncListenerRef.current, true);
                capsLockSyncListenerRef.current = null;
            }
            setIsVncConnected(false);
            setUiChannelReady(false);
        };        
        
        rfb.addEventListener("connect", handleConnect);
        rfb.addEventListener("disconnect", handleDisconnect);
        
        return () => {
            if (connectionTimeoutRef.current) {
                clearTimeout(connectionTimeoutRef.current);
                connectionTimeoutRef.current = null;
            }
            if (capsLockSyncListenerRef.current) {
                document.removeEventListener('keydown', capsLockSyncListenerRef.current, true);
                capsLockSyncListenerRef.current = null;
            }
            rfb.removeEventListener("connect", handleConnect);
            rfb.removeEventListener("disconnect", handleDisconnect);
            
            if (rfbRef.current) {
                rfbRef.current.disconnect();
            }
            if (uiEventsWsRef.current) {
                uiEventsWsRef.current.close();
                uiEventsWsRef.current = null;
            }
        }
        
        // Intentionally exclude `currentStoreStatus` and `setupUiEventChannel`
        // from deps: both change frequently during a session and re-mounting
        // the WS on every change tears down the live VNC connection mid-flow.
        // They are accessed via refs above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store_id, activeToken, viewerElement, isMobile, token, retryKey]);

    // useEffect(() => {
    //     if (!openState || !store_id || !activeToken) return;

    //     const ws = new WebSocket(remoteBrowserUiEventsEndpoint(activeToken, store_id, isMobile));
    //     uiEventsWsRef.current = ws;

    //     ws.onmessage = (event) => {
    //         const data = parseUiEventMessage(event.data);
    //         if (!data) return;

    //         const payloadTextInput = data.payload?.in_text_input;
    //         const payloadKeyboardHint = data.payload?.show_keyboard;
    //         const isTextInputFocused = payloadTextInput ?? data.in_text_input ?? data.value;

    //         if (data.event === 'TEXT_INPUT_FOCUS' || isTextInputFocused === true) {
    //             setIsRemoteTextInputFocused(true);
    //             return;
    //         }

    //         if (data.event === 'TEXT_INPUT_BLUR' || isTextInputFocused === false) {
    //             setIsRemoteTextInputFocused(false);
    //             return;
    //         }

    //         if (data.event === 'SHOW_KEYBOARD' || payloadKeyboardHint === true) {
    //             setIsRemoteTextInputFocused(true);
    //             return;
    //         }

    //         if (data.event === 'HIDE_KEYBOARD' || payloadKeyboardHint === false) {
    //             setIsRemoteTextInputFocused(false);
    //         }
    //     };

    //     ws.onerror = (error) => {
    //         console.error('Remote browser UI events websocket error:', error);
    //     };

    //     ws.onclose = () => {
    //         if (uiEventsWsRef.current === ws) {
    //             uiEventsWsRef.current = null;
    //         }
    //     };

    //     return () => {
    //         if (uiEventsWsRef.current === ws) {
    //             uiEventsWsRef.current = null;
    //         }
    //         ws.close();
    //     };
    // }, [openState, store_id, activeToken, isMobile]);


    const getScaledCoordinates = (event: React.MouseEvent<HTMLImageElement> | React.TouchEvent<HTMLImageElement>) => {
        const img = event.currentTarget;
        const rect = img.getBoundingClientRect();
        
        let clientX: number;
        let clientY: number;

        // Discriminador de tipos: ¿Es un evento táctil o de mouse?
        if ('touches' in event) {
            // Truco Pro: En onTouchEnd, 'touches' queda vacío porque ya no hay dedos en la pantalla.
            // Para ese caso, leemos 'changedTouches' que guarda la última posición del dedo.
            const touch = event.touches[0] || event.changedTouches[0];
            clientX = touch.clientX;
            clientY = touch.clientY;
        } else {
            // Es un evento de Mouse tradicional
            clientX = event.clientX;
            clientY = event.clientY;
        }
        
        // Tu excelente lógica de escalado se mantiene intacta
        const scaleX = img.naturalWidth / rect.width;
        const scaleY = img.naturalHeight / rect.height;

        // Multiplicamos la coordenada relativa por la escala
        const x = Math.round((clientX - rect.left) * scaleX);
        const y = Math.round((clientY - rect.top) * scaleY);

        return { x, y };
    };    

    const handleMouseDown = (event: React.MouseEvent<HTMLImageElement> | React.TouchEvent<HTMLImageElement>) => {
        // // Si es un evento touch, guardamos el timestamp
        // if (event.type.startsWith('touch')) {
        //     lastTouchTime.current = Date.now();
        // } else {
        //     // Si es un evento mouse y hubo un touch reciente (< 500ms), lo ignoramos
        //     if (Date.now() - lastTouchTime.current < 500) return;
        // }

        // lastMoveTime.current = Date.now();
        // if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        //     isPressing.current = true;
        //     const { x, y } = getScaledCoordinates(event);
        //     ws.current.send(JSON.stringify({ type: 'click', x, y }));
        // }
    };

    const handleMouseUp = (event: React.MouseEvent<HTMLImageElement> | React.TouchEvent<HTMLImageElement>) => {
        // // Si es un evento touch, guardamos el timestamp
        // if (event.type.startsWith('touch')) {
        //     lastTouchTime.current = Date.now();
        // } else {
        //     // Si es un evento mouse y hubo un touch reciente (< 500ms), lo ignoramos
        //     if (Date.now() - lastTouchTime.current < 500) return;
        // }

        // if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        //     if (!isPressing.current) return;
        //     isPressing.current = false;
        //     const { x, y } = getScaledCoordinates(event);
        //     ws.current.send(JSON.stringify({ type: 'release', x, y }));
        // }
    };
    const handleMouseMove = (event: React.MouseEvent<HTMLImageElement> | React.TouchEvent<HTMLImageElement>) => {
        // // Si es touch, actualizamos timestamp; si es mouse y hubo touch reciente, ignoramos
        // if (event.type.startsWith('touch')) {
        //     lastTouchTime.current = Date.now();
        // } else {
        //     if (Date.now() - lastTouchTime.current < 500) return;
        // }

        // if (!isPressing.current) return;

        // const now = Date.now();        
        // // Si no ha pasado suficiente tiempo desde el último evento, lo ignoramos
        // if (now - lastMoveTime.current < THROTTLE_MS) return; 
        
        // lastMoveTime.current = now;
        // if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        //     const { x, y } = getScaledCoordinates(event);
        //     ws.current.send(JSON.stringify({ type: 'move', x, y }));
        // }
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        //     ws.current.send(JSON.stringify({ type: 'keydown', key: event.key }));
        // }
    };

    const handleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
        // if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        //     ws.current.send(JSON.stringify({ type: 'keyup', key: event.key }));
        // }
    };

    // Manejo de input desde el teclado nativo del teléfono (input oculto)
    const handleHiddenInput = (event: React.FormEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const value = input.value;
        if (!value) return;

        for (const char of value) {
            sendKeyToRemote(char);
        }

        // Limpiar para capturar solo el siguiente lote de caracteres.
        input.value = '';
    };

    const handleHiddenBeforeInput = (event: React.FormEvent<HTMLInputElement>) => {
        const nativeEvent = event.nativeEvent as InputEvent;

        if (nativeEvent.inputType === 'deleteContentBackward') {
            event.preventDefault();
            sendKeyToRemote('Backspace');
            return;
        }

        if (nativeEvent.inputType === 'insertLineBreak') {
            event.preventDefault();
            sendKeyToRemote('Enter');
            return;
        }

        if (!nativeEvent.data) return;

        event.preventDefault();
        for (const char of nativeEvent.data) {
            sendKeyToRemote(char);
        }
    };

    const handleHiddenKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!(event.key in specialKeysyms)) return;

        event.preventDefault();
        sendKeyToRemote(event.key);
    };


    // const disconnectWebSocket = () => {
    //     if (ws.current) {
    //         ws.current.close();
    //         ws.current = null;
    //     }
    // }

    return(    
    <div className={cn("", className)} ref={ref}>
        <Sheet open={openState} onOpenChange={(open) => handleClose(open)}>
        <SheetContent side={"bottom"} className="flex h-[95dvh] flex-col gap-0 overflow-hidden p-0 bg-gradient-to-br from-[#7a3506] via-[#cb5c0b] to-[#e8873d]" >

            {/* Cloud background con profundidad */}
            <div className={cn("relative flex min-h-0 flex-1 flex-col items-center justify-center w-full p-4 md:p-8",{
            })}
                style={{ background: 'linear-gradient(135deg, #7a3506 0%, #CB5C0B 50%, #e8873d 100%)' }}>

                {/* Ambient blur blobs */}
                <div className="absolute top-[-10%] left-[-10%] w-64 h-64 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(203,92,11,0.35)' }} />
                <div className="absolute bottom-[-5%] right-[-5%] w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(100,30,5,0.4)' }} />
                <div className="absolute top-[30%] right-[-8%] w-48 h-48 rounded-full blur-2xl pointer-events-none" style={{ background: 'rgba(232,135,61,0.25)' }} />

                {/* Ventana del navegador */}
                <div className={cn("relative w-full min-w-0 flex flex-col rounded-xl overflow-hidden z-10 shadow-[0_32px_80px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.08)]",
                    {
                        "max-w-2xl": isMobile,
                        "max-w-[80%]": !isMobile,
                    }
                )}>

                    {/* Sombra ambiental flotante */}
                    <div className="absolute inset-x-4 bottom-0 h-8 bg-black/40 blur-xl translate-y-4 rounded-full pointer-events-none -z-10" />

                    {/* Barra de tabs */}
                    <div className="bg-[#2b2d31] flex items-end gap-0 px-3 pt-2 select-none">
                        {/* Tab activa */}
                        <div className="flex items-center gap-2 bg-[#f1f3f5] rounded-t-lg px-3 py-1.5 min-w-0 max-w-[220px]">
                            <Avatar key={props.storeName} className="h-4 w-4 shrink-0">
                                <AvatarImage src={props?.storeLogo ?? ""} alt={props?.storeName ?? ""} />
                                <AvatarFallback className="text-[8px] bg-gray-300">
                                    {props?.storeName?.slice(0,2).toUpperCase() ?? "??"}
                                </AvatarFallback>
                            </Avatar>
                            <span className="text-[11px] text-gray-700 truncate font-medium">{props.storeName}</span>
                        </div>
                        {/* Relleno a la derecha */}
                        <div className="flex-1" />
                        {/* Botón cerrar */}
                        <button
                            className="flex items-center justify-center pb-2 text-white/50 hover:text-white transition-colors"
                            tabIndex={-1}
                            onClick={() => handleClose(false)}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Contenido — screencast o loader */}
                    <div
                        data-testid="remote-browser-scroll-container"
                        className="bg-white w-full overflow-x-hidden overflow-y-auto relative transition-all duration-700 ease-in-out"
                        style={imgNaturalSize
                            ? { aspectRatio: `${imgNaturalSize.w} / ${imgNaturalSize.h}`, maxHeight: '85dvh' }
                            : { height: '85dvh' }}
                    >
                        {/* Loading/error overlay — visible while VNC is not yet connected */}
                        {(!isVncConnected || isConnectionInterrupted) && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-white">
                                {isConnectionInterrupted ? (
                                    /* Session interrupted state — server closed an active connection */
                                    <>
                                        <div className="flex flex-col items-center gap-3">
                                            <WifiOff className="w-10 h-10 text-[#CB5C0B]" />
                                            <div className="flex flex-col items-center gap-1 text-center px-6">
                                                <span className="text-sm font-semibold text-gray-800">
                                                    Sesión interrumpida
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    El tiempo de la sesión se agotó. Podés reintentar para iniciar una nueva.
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsConnectionInterrupted(false);
                                                setRetryKey((k) => k + 1);
                                            }}
                                            className="flex items-center gap-2 rounded-full bg-[#CB5C0B] px-5 py-2 text-xs font-semibold text-white shadow-md hover:bg-[#a84d09] active:scale-95 transition-all"
                                        >
                                            <RefreshCw className="w-3.5 h-3.5" />
                                            Reintentar
                                        </button>
                                    </>
                                ) : isConnectionError ? (
                                    /* Error state — never reached VNC */
                                    <>
                                        <div className="flex flex-col items-center gap-3">
                                            <AlertCircle className="w-10 h-10 text-[#CB5C0B]" />
                                            <div className="flex flex-col items-center gap-1 text-center px-6">
                                                <span className="text-sm font-semibold text-gray-800">
                                                    No se pudo conectar
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    El navegador remoto no respondió. Intenta de nuevo.
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsConnectionError(false);
                                                setRetryKey((k) => k + 1);
                                            }}
                                            className="flex items-center gap-2 rounded-full bg-[#CB5C0B] px-5 py-2 text-xs font-semibold text-white shadow-md hover:bg-[#a84d09] active:scale-95 transition-all"
                                        >
                                            <RefreshCw className="w-3.5 h-3.5" />
                                            Reintentar
                                        </button>
                                    </>
                                ) : (
                                    /* Spinner state */
                                    <>
                                        <div className="relative w-14 h-14">
                                            {/* Outer rotating ring */}
                                            <div className="absolute inset-0 rounded-full border-4 border-orange-100" />
                                            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#CB5C0B] animate-spin" />
                                            {/* Store avatar in the center */}
                                            <div className="absolute inset-[6px] rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                                                <Avatar className="h-full w-full">
                                                    <AvatarImage src={props?.storeLogo ?? ""} alt={props?.storeName ?? ""} />
                                                    <AvatarFallback className="text-[10px] bg-gray-200 text-gray-600">
                                                        {props?.storeName?.slice(0,2).toUpperCase() ?? "??"}
                                                    </AvatarFallback>
                                                </Avatar>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="text-sm font-medium text-gray-700">
                                                Navegando a {props.storeName}
                                            </span>
                                            <span className="text-xs text-gray-400">
                                                {initStatusMessage ?? 'Conectando sesión segura…'}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        <div
                            className="w-full h-full max-w-full max-h-full overflow-hidden m-0 p-0 leading-none flex [&_*]:m-0 [&_*]:p-0 [&_canvas]:block"
                            onTouchEnd={handleViewerTouchEnd}
                            ref={setViewerElement}
                        />
{/* 
                        {shouldShowLoginCompletionBanner && (
                            <div
                                aria-hidden="true"
                                data-testid="remote-browser-bottom-spacer"
                                className="h-28 shrink-0 sm:h-20 background: linear-gradient(135deg, rgb(122, 53, 6) 0%, rgb(203, 92, 11) 50%, rgb(232, 135, 61) 100%);"
                            />
                        )} */}
                    </div>

                </div>{/* fin ventana browser */}

                {/* Indicador de teclado virtual — tappable para abrir teclado nativo */}
                {
                isVncConnected&&(
                    <OpenKeyBoardButton 
                        keyboardVisible = {keyboardVisible}
                        keyboardFocused = {keyboardFocused}
                        blurHiddenInput = {blurHiddenInput}
                        focusHiddenInput = {focusHiddenInput}                
                    />
                )

                }

                {/* <div className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ease-in-out
                                ${keyboardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                    <button
                        type="button"
                        onClick={keyboardFocused ? blurHiddenInput : focusHiddenInput}
                        className={`flex items-center gap-2 backdrop-blur-md text-white rounded-full
                                    px-4 py-2.5 shadow-lg shadow-black/30 border transition-colors duration-200
                                    ${keyboardFocused
                                        ? 'bg-orange-600/80 border-orange-400/40'
                                        : 'bg-black/70 border-white/10'}`}
                    >
                        <Keyboard className={`w-4 h-4 ${keyboardFocused ? 'text-white' : 'text-orange-300 animate-pulse'}`} />
                        <span className="text-xs font-medium">
                            {keyboardFocused ? 'Escribiendo…' : 'Abrir teclado'}
                        </span>
                    </button>
                </div> */}

                {/* Input oculto para capturar el teclado nativo del teléfono */}
                <input
                    ref={hiddenInputRef}
                    type="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    className="fixed -top-[200px] left-0 w-px h-px opacity-0 pointer-events-none"
                    onBeforeInput={handleHiddenBeforeInput}
                    onInput={handleHiddenInput}
                    onKeyDown={handleHiddenKeyDown}
                    onBlur={() => setKeyboardFocused(false)}
                    onFocus={() => setKeyboardFocused(true)}
                    aria-label="Teclado virtual para escritura remota"
                />

            </div>{/* fin cloud bg */}

            {/* Login completion banner — anchored to the sheet frame, outside the remote browser scroll area */}
            {shouldShowLoginCompletionBanner && (
                <div
                    role="region"
                    aria-label="Confirmar inicio de sesión en tienda"
                    className="bg-white relative z-30 flex shrink-0 flex-col gap-3 px-4 py-3 items-center"
                >
                    <p className="text-xs font-medium leading-snug text-center">
                        <Lock size="4" className="inline-block mr-2 w-4 h-4 text-primary"/>Inicia sesión y verifica tu dirección de despacho, luego presiona el botón para continuar.
                    </p>
                    <Button
                        size="default"
                        onClick={onConfirmSessionReady}
                        className="w-fit px-8"
                    >
                        <Check />Ya inicié sesión en {storeDisplayName}, continuar
                    </Button>
                </div>
            )}

        </SheetContent>
        </Sheet>
    </div>
);
});
